import { pickedDay } from "@/lib/clinic-clock";
import { getDefaultDateRange } from "@/lib/date-range-presets";
import { getTimezone } from "@/lib/timezone";

import type { AppointmentDashboardFilters } from "@/types/appointment-dashboard";
import type { PatientSubmissionsFetchParams } from "@/types/forms/form";

import type { ClockInDashboardParams } from "@/api/analytics/dashboards/clock-in";
import {
  booleanSetFilter,
  dateRangeFilter,
  defineFilters,
  enumSetFilter,
  idSetFilter,
  normalizeFilterRequest,
  numberFilter,
  serializeFilterQuerySegments,
  setFilter,
  textFilter,
  tupleDateRangeFilter,
} from "@/features/table-filters/contract";
import type { FilterKey } from "@/features/table-filters/contract";

function tableStateDefinitions<Request extends object>() {
  return [
    numberFilter<Request>({
      key: "page" as FilterKey<Request>,
      label: "Page",
      presented: false,
      urlPolicy: "none",
      queryKey: "page",
    }),
    numberFilter<Request>({
      key: "perPage" as FilterKey<Request>,
      label: "Per page",
      presented: false,
      urlPolicy: "none",
      queryKey: "per_page",
    }),
    textFilter<Request>({
      key: "sortBy" as FilterKey<Request>,
      label: "Sort by",
      presented: false,
      urlPolicy: "none",
      queryKey: "sort_by",
    }),
    textFilter<Request>({
      key: "sortDirection" as FilterKey<Request>,
      label: "Sort direction",
      presented: false,
      urlPolicy: "none",
      queryKey: "sort_direction",
    }),
    textFilter<Request>({
      key: "tz" as FilterKey<Request>,
      label: "Timezone",
      presented: false,
      urlPolicy: "none",
      queryKey: "tz",
    }),
  ];
}

export const clockInHistoryFilterDefinitions =
  defineFilters<ClockInDashboardParams>()([
    setFilter<ClockInDashboardParams>({
      key: "providerIds",
      label: "Providers",
      queryKey: "provider_ids",
    }),
    setFilter<ClockInDashboardParams>({
      key: "locationIds",
      label: "Locations",
      control: "location",
      queryKey: "location_ids",
    }),
    textFilter<ClockInDashboardParams>({
      key: "startDate",
      label: "Start date",
      presented: false,
      urlPolicy: "none",
      queryKey: "start_date",
    }),
    textFilter<ClockInDashboardParams>({
      key: "endDate",
      label: "End date",
      presented: false,
      urlPolicy: "none",
      queryKey: "end_date",
    }),
    textFilter<ClockInDashboardParams>({
      key: "timezone",
      label: "Timezone",
      presented: false,
      urlPolicy: "none",
      queryKey: "timezone",
    }),
    ...tableStateDefinitions<ClockInDashboardParams>(),
    textFilter<ClockInDashboardParams>({
      key: "groupBy" as FilterKey<ClockInDashboardParams>,
      label: "Group by",
      presented: false,
      urlPolicy: "none",
      queryKey: "group_by",
    }),
    textFilter<ClockInDashboardParams>({
      key: "groupKey" as FilterKey<ClockInDashboardParams>,
      label: "Group key",
      presented: false,
      urlPolicy: "none",
      queryKey: "group_key",
    }),
  ]);

export const appointmentDashboardFilterDefinitions =
  defineFilters<AppointmentDashboardFilters>()([
    dateRangeFilter<AppointmentDashboardFilters>({
      startKey: "startDate",
      endKey: "endDate",
      label: "Date range",
      startQueryKey: "start_date",
      endQueryKey: "end_date",
      presented: false,
    }),
    setFilter<AppointmentDashboardFilters>({
      key: "providerIds",
      label: "Providers",
      queryKey: "provider_ids",
    }),
    setFilter<AppointmentDashboardFilters>({
      key: "locationIds",
      label: "Locations",
      control: "location",
      queryKey: "location_ids",
    }),
  ]);

export function getDefaultAppointmentDashboardFilters(
  timeZone: string = getTimezone(),
): AppointmentDashboardFilters {
  const defaultRange = getDefaultDateRange(timeZone);
  return {
    startDate: pickedDay(defaultRange.from!),
    endDate: pickedDay(defaultRange.to!),
  };
}

export function normalizeAppointmentDashboardFilters(
  filters: Partial<AppointmentDashboardFilters>,
): Partial<AppointmentDashboardFilters> {
  return normalizeFilterRequest(appointmentDashboardFilterDefinitions, filters);
}

export function serializeAppointmentDashboardFilters(
  filters: Partial<AppointmentDashboardFilters>,
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: appointmentDashboardFilterDefinitions, values: filters },
  ]);
}

export const patientSubmissionFilterDefinitions =
  defineFilters<PatientSubmissionsFetchParams>()([
    ...tableStateDefinitions<PatientSubmissionsFetchParams>(),
    textFilter<PatientSubmissionsFetchParams>({
      key: "patientName",
      label: "Patient Name",
      operator: "contains",
      queryKey: "patient_name",
    }),
    idSetFilter<PatientSubmissionsFetchParams>({
      key: "id",
      label: "Submission ID",
    }),
    textFilter<PatientSubmissionsFetchParams>({
      key: "patientId",
      label: "Patient ID",
      queryKey: "patient_id",
    }),
    textFilter<PatientSubmissionsFetchParams>({
      key: "phoneNumber",
      label: "Phone Number",
      operator: "contains",
      queryKey: "phone_number",
    }),
    tupleDateRangeFilter<PatientSubmissionsFetchParams>({
      key: "submissionDate",
      label: "Submission Date",
      queryKey: "submission_date",
    }),
    setFilter<PatientSubmissionsFetchParams>({
      key: "formIds",
      label: "By Form",
      queryKey: "form_ids",
    }),
    booleanSetFilter<PatientSubmissionsFetchParams>({
      key: "hadUploadError",
      label: "Requires Follow-up",
      queryKey: "had_upload_error",
      options: [
        { value: "true", label: "Upload Issues Only" },
        { value: "false", label: "No Issues Only" },
      ],
    }),
    // Stays a multiselect: the page defaults it to complete submissions, so
    // clearing restores that default. Ticking both is the only way to see all.
    enumSetFilter<PatientSubmissionsFetchParams>({
      key: "isCompleted",
      label: "Completion Status",
      queryKey: "is_completed",
      options: [
        { value: "true", label: "Complete Submissions" },
        { value: "false", label: "Partial Submissions" },
      ],
    }),
  ]);

export function serializeClockInDashboardFilters(
  params: ClockInDashboardParams & { groupBy?: string; groupKey?: string },
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: clockInHistoryFilterDefinitions, values: params },
  ]);
}

export function serializePatientSubmissionFilters(
  params: PatientSubmissionsFetchParams,
): URLSearchParams {
  return serializeFilterQuerySegments([
    {
      definitions: patientSubmissionFilterDefinitions,
      values: { ...params, tz: params.tz ?? getTimezone() },
    },
  ]);
}
